
import { LoginCredentials, SignupData, VerificationData, ResetPasswordData, InvitePasswordResetData, AuthUser } from '../types';
import CryptoJS from 'crypto-js';
import { api } from '../../../core/utils/apiClient';
import { setSessionToken, getSessionToken, clearSession, TOKEN_KEYS } from '../../../core/utils/sessionStorage.util';


const calculateSecretHash = (username: string): string => {
  const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID || '';
  const clientSecret = process.env.REACT_APP_CLIENT_SECRET || '';

  // If no client secret, return empty string (public client)
  if (!clientSecret) {
    return '';
  }

  const message = username + clientId;
  const hash = CryptoJS.HmacSHA256(message, clientSecret);
  return CryptoJS.enc.Base64.stringify(hash);
};

export class AuthService {
  async login(credentials: LoginCredentials): Promise<void> {
    const secretHash = calculateSecretHash(credentials.email);

    // Use fetch API to call Cognito directly
    const response = await fetch(`https://cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        AuthParameters: {
          USERNAME: credentials.email,
          PASSWORD: credentials.password,
          ...(secretHash && { SECRET_HASH: secretHash }),
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    // Store tokens in sessionStorage
    if (data.AuthenticationResult) {
      console.log('[Auth Service] ✅ Login successful, storing tokens');
      console.log('[Auth Service] Has ID Token:', !!data.AuthenticationResult.IdToken);
      console.log('[Auth Service] Has Access Token:', !!data.AuthenticationResult.AccessToken);
      console.log('[Auth Service] Has Refresh Token:', !!data.AuthenticationResult.RefreshToken);

      setSessionToken(TOKEN_KEYS.ID_TOKEN, data.AuthenticationResult.IdToken);
      setSessionToken(TOKEN_KEYS.ACCESS_TOKEN, data.AuthenticationResult.AccessToken);
      setSessionToken(TOKEN_KEYS.REFRESH_TOKEN, data.AuthenticationResult.RefreshToken);

      console.log('[Auth Service] Tokens stored in sessionStorage');

      // Fetch user info from API and store it
      await this.fetchAndStoreUserInfo();
    } else {
      console.error('[Auth Service] ❌ No AuthenticationResult in login response');
    }
  }

  async signup(data: SignupData): Promise<void> {
    const secretHash = calculateSecretHash(data.email);

    const response = await fetch(`https://cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
      },
      body: JSON.stringify({
        ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        Username: data.email,
        Password: data.password,
        ...(secretHash && { SecretHash: secretHash }),
        UserAttributes: [
          { Name: 'email', Value: data.email },
          { Name: 'name', Value: data.name },
        ],
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Signup failed');
    }
  }

  async confirmSignup(data: VerificationData): Promise<void> {
    const secretHash = calculateSecretHash(data.email);

    const response = await fetch(`https://cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmSignUp',
      },
      body: JSON.stringify({
        ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        Username: data.email,
        ConfirmationCode: data.code,
        ...(secretHash && { SecretHash: secretHash }),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Confirmation failed');
    }
  }

  async logout(): Promise<void> {
    console.log('[Auth Service] 🚪 Logout called');
    console.trace('[Auth Service] Logout stack trace'); // This will show where logout was called from
    clearSession();
    localStorage.removeItem('userInfo');
    localStorage.removeItem('enrichmentStatus');
    console.log('[Auth Service] Tokens cleared from sessionStorage, user info cleared from localStorage');
  }

  async forgotPassword(email: string): Promise<void> {
    const secretHash = calculateSecretHash(email);

    const response = await fetch(`https://cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword',
      },
      body: JSON.stringify({
        ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        Username: email,
        ...(secretHash && { SecretHash: secretHash }),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send reset code');
    }
  }

  async confirmForgotPassword(data: ResetPasswordData): Promise<void> {
    const secretHash = calculateSecretHash(data.email);

    const response = await fetch(`https://cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
      },
      body: JSON.stringify({
        ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        Username: data.email,
        ConfirmationCode: data.code,
        Password: data.newPassword,
        ...(secretHash && { SecretHash: secretHash }),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Password reset failed');
    }
  }

  /**
   * Handles password reset for invited users with temporary passwords.
   * Uses NEW_PASSWORD_REQUIRED challenge flow (no code needed).
   */
  async resetPasswordWithTemporary(data: InvitePasswordResetData): Promise<void> {
    const secretHash = calculateSecretHash(data.email);

    // Step 1: Initiate auth with temporary password
    // This will trigger NEW_PASSWORD_REQUIRED challenge
    const initiateResponse = await fetch(`https://cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
        AuthParameters: {
          USERNAME: data.email,
          PASSWORD: data.temporaryPassword,
          ...(secretHash && { SECRET_HASH: secretHash }),
        },
      }),
    });

    if (!initiateResponse.ok) {
      const error = await initiateResponse.json();
      throw new Error(error.message || 'Invalid temporary password');
    }

    const initiateData = await initiateResponse.json();

    // Check if we got a NEW_PASSWORD_REQUIRED challenge
    if (initiateData.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      // Step 2: Respond to the challenge with new password
      const challengeResponse = await fetch(`https://cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
        },
        body: JSON.stringify({
          ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
          ChallengeName: 'NEW_PASSWORD_REQUIRED',
          Session: initiateData.Session,
          ChallengeResponses: {
            USERNAME: data.email,
            NEW_PASSWORD: data.newPassword,
            ...(secretHash && { SECRET_HASH: secretHash }),
          },
        }),
      });

      if (!challengeResponse.ok) {
        const error = await challengeResponse.json();
        throw new Error(error.message || 'Failed to set new password');
      }

      const challengeData = await challengeResponse.json();

      // Store tokens if authentication was successful
      if (challengeData.AuthenticationResult) {
        console.log('[Auth Service] ✅ Password reset successful, storing tokens');
        setSessionToken(TOKEN_KEYS.ID_TOKEN, challengeData.AuthenticationResult.IdToken);
        setSessionToken(TOKEN_KEYS.ACCESS_TOKEN, challengeData.AuthenticationResult.AccessToken);
        setSessionToken(TOKEN_KEYS.REFRESH_TOKEN, challengeData.AuthenticationResult.RefreshToken);

        // Fetch user info
        await this.fetchAndStoreUserInfo();
        localStorage.setItem('enrichmentStatus', 'pending');
      }
    } else if (initiateData.AuthenticationResult) {
      // User already has a permanent password (shouldn't happen for invited users)
      // But if it does, just store tokens
      console.log('[Auth Service] ✅ User already has permanent password');
      setSessionToken(TOKEN_KEYS.ID_TOKEN, initiateData.AuthenticationResult.IdToken);
      setSessionToken(TOKEN_KEYS.ACCESS_TOKEN, initiateData.AuthenticationResult.AccessToken);
      setSessionToken(TOKEN_KEYS.REFRESH_TOKEN, initiateData.AuthenticationResult.RefreshToken);
      await this.fetchAndStoreUserInfo();
      localStorage.setItem('enrichmentStatus', 'pending');
    } else {
      throw new Error('Unexpected response from Cognito');
    }
  }

  /**
   * Fetches user info from /api/v1/auth/me and stores it in localStorage
   */
  async fetchAndStoreUserInfo(): Promise<AuthUser> {
    try {
      const response = await api.get('/api/v1/auth/me');

      if (!response.ok) {
        throw new Error('Failed to fetch user info');
      }

      const userData = await response.json();
      const userInfo: AuthUser = {
        id: userData.id,
        email: userData.email,
        username: userData.username,
        full_name: userData.full_name,
        name: userData.full_name || userData.name || '',
        groups: userData.groups || [],
        permissions: userData.permissions || {},
        email_verified: userData.email_verified || false,
        // Check permissions for partner access (partner OR company_user)
        is_partner:
          userData.permissions?.is_partner === true ||
          userData.permissions?.is_company_user === true ||
          false,
        has_company_admin_access: userData.has_company_admin_access === true,
        administered_companies: userData.administered_companies ?? [],
        has_company_access: userData.has_company_access === true,
        member_companies: userData.member_companies ?? [],
        // Partners/members have no top-level company_id; derive it from the companies
        // they administer, falling back to any company they're a member of.
        company_id:
          userData.company_id ??
          userData.company?.id ??
          userData.administered_companies?.[0]?.company_id ??
          userData.member_companies?.[0]?.company_id ??
          null,
        enrichment_status: userData.enrichment_status ?? null,
        active_enrichment_session_id: userData.active_enrichment_session_id ?? null,
        enrichment_progress_percent: userData.enrichment_progress_percent ?? null,
      };

      // Store in localStorage
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      console.log('[Auth Service] ✅ User info fetched and stored:', userInfo);

      if (userInfo.enrichment_status) {
        localStorage.setItem('enrichmentStatus', userInfo.enrichment_status);
      }

      return userInfo;
    } catch (error) {
      console.error('[Auth Service] ❌ Failed to fetch user info:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<AuthUser> {
    const idToken = getSessionToken(TOKEN_KEYS.ID_TOKEN);
    console.log('[Auth Service] getCurrentUser called, has token:', !!idToken);

    if (!idToken) {
      console.warn('[Auth Service] No ID token found in sessionStorage');
      throw new Error('Not authenticated');
    }

    try {
      // First, try to get user info from localStorage
      const storedUserInfo = localStorage.getItem('userInfo');
      if (storedUserInfo) {
        const userInfo = JSON.parse(storedUserInfo) as AuthUser;
        console.log('[Auth Service] ✅ Using stored user info:', userInfo);
        return userInfo;
      }

      // If not in localStorage, fetch from API
      console.log('[Auth Service] No stored user info, fetching from API...');
      return await this.fetchAndStoreUserInfo();
    } catch (error) {
      console.error('[Auth Service] ❌ Failed to get user info:', error);

      // Fallback: decode from token if API call fails
      try {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        console.log('[Auth Service] ⚠️ Falling back to token decode');

        const groups = payload['cognito:groups'] || payload.groups || [];
        const is_partner = groups.includes('partner') || groups.includes('admin') || groups.includes('company_user');

        return {
          id: payload.sub || '',
          email: payload.email || '',
          username: payload['cognito:username'] || payload.email || '',
          full_name: payload.name || payload.given_name || '',
          name: payload.name || payload.given_name || '',
          groups: groups,
          email_verified: payload.email_verified || false,
          is_partner: is_partner,
        };
      } catch (decodeError) {
        console.error('[Auth Service] ❌ Failed to decode token:', decodeError);
        throw new Error('Invalid token');
      }
    }
  }

  // Debug method to get tokens
  getTokens() {
    return {
      accessToken: getSessionToken(TOKEN_KEYS.ACCESS_TOKEN),
      idToken: getSessionToken(TOKEN_KEYS.ID_TOKEN),
      refreshToken: getSessionToken(TOKEN_KEYS.REFRESH_TOKEN),
    };
  }

  // Get the token used for API calls (ID Token)
  getApiToken() {
    return getSessionToken(TOKEN_KEYS.ID_TOKEN);
  }

  // Check if token is expired
  isTokenExpired(): boolean {
    const idToken = getSessionToken(TOKEN_KEYS.ID_TOKEN);
    if (!idToken) return true;

    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      // Consider token expired if it expires in less than 5 minutes
      return exp - now < 5 * 60 * 1000;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  // Refresh the token using the refresh token via backend endpoint
  async refreshToken(): Promise<void> {
    console.log('[Auth Service] Starting token refresh...');
    const refreshToken = getSessionToken(TOKEN_KEYS.REFRESH_TOKEN);
    const currentIdToken = getSessionToken(TOKEN_KEYS.ID_TOKEN);

    if (!refreshToken) {
      console.error('[Auth Service] No refresh token available');
      throw new Error('No refresh token available');
    }

    if (!currentIdToken) {
      console.error('[Auth Service] No ID token available');
      throw new Error('No ID token available');
    }

    try {
      // Use direct fetch to avoid going through apiClient which might cause issues
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const refreshUrl = `${API_BASE_URL}/api/v1/auth/refresh`;

      console.log('[Auth Service] Calling refresh endpoint:', refreshUrl);

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
          current_id_token: currentIdToken,
        }),
      });

      console.log('[Auth Service] Refresh response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Token refresh failed';
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        console.error('[Auth Service] Token refresh failed:', errorMessage);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('[Auth Service] Refresh response data keys:', Object.keys(data));

      // Update tokens in sessionStorage
      if (data.id_token) {
        console.log('[Auth Service] ✅ Token refresh successful');
        setSessionToken(TOKEN_KEYS.ID_TOKEN, data.id_token);

        // Update access token if provided
        if (data.access_token) {
          setSessionToken(TOKEN_KEYS.ACCESS_TOKEN, data.access_token);
        }

        // Note: Refresh token is typically not returned in refresh response
        // Keep the existing refresh token unless a new one is provided
        if (data.refresh_token) {
          setSessionToken(TOKEN_KEYS.REFRESH_TOKEN, data.refresh_token);
        }

        // Optionally refresh user info after token refresh
        try {
          await this.fetchAndStoreUserInfo();
        } catch (error) {
          console.warn('[Auth Service] Failed to refresh user info after token refresh:', error);
          // Don't throw - token refresh was successful
        }
      } else {
        console.error('[Auth Service] No id_token in refresh response');
        throw new Error('Invalid refresh response');
      }
    } catch (error: any) {
      console.error('[Auth Service] Token refresh error:', error);
      // If refresh fails, clear tokens to force re-login
      if (error.message?.includes('refresh') || error.message?.includes('expired') || error.message?.includes('Failed to fetch')) {
        console.log('[Auth Service] Refresh token expired or invalid, clearing tokens');
        this.logout();
      }
      throw error;
    }
  }
}

export const authService = new AuthService();